import React, { type SyntheticEvent } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import { transformProjectType } from '@atlassian/jira-issue-create-commons';
import {
	ContextualAnalyticsData,
	MountEvent,
	MODAL,
	fireScreenAnalyticsDeferred,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { CONFIRM_DISMISS_MODAL_DISCARD_ISSUE_BUTTON } from '../../../../common/constants/selectors';
import messages from './messages';
import type { Props } from './types';

const ConfirmDismissModal = (props: Props) => {
	const { formatMessage } = useIntl();
	const { project, onCancel = noop, onConfirm = noop, sessionId } = props;
	const { projectId, projectType } = project;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onClickCancelButton = (_: any, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'cancelCloseCreateModal', {
			projectType: transformProjectType(projectType),
		});
		onCancel();
	};

	const onClickConfirmButton = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'continueCloseCreateModal', {
			projectType: transformProjectType(projectType),
			gicSessionId: sessionId,
		});
		onConfirm(event, analyticsEvent);
	};
	return (
		<ContextualAnalyticsData
			sourceType={MODAL}
			sourceName="cancelIssueCreate"
			containerType="project"
			containerId={projectId || ''}
			attributes={{ projectType: transformProjectType(projectType) }}
		>
			<MountEvent onMount={fireScreenAnalyticsDeferred} />
			<ShortcutScope>
				<ModalDialog onClose={onCancel} width="small">
					<ModalHeader>
						<ModalTitle>{formatMessage(messages.title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>{formatMessage(messages.description)}</ModalBody>

					<ModalFooter>
						<Button appearance="subtle-link" onClick={onClickCancelButton}>
							{formatMessage(messages.cancelButtonLabel)}
						</Button>
						<Button
							appearance="primary"
							onClick={onClickConfirmButton}
							testId={CONFIRM_DISMISS_MODAL_DISCARD_ISSUE_BUTTON}
						>
							{formatMessage(messages.confirmButtonLabel)}
						</Button>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ContextualAnalyticsData>
	);
};
export default ConfirmDismissModal;
